const safeArray = (value) => (Array.isArray(value) ? value : []);

const uniqStrings = (values) =>
  Array.from(
    new Set(
      safeArray(values)
        .filter(Boolean)
        .map((value) => String(value).trim())
        .filter(Boolean)
    )
  );

const toStringArray = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  if (value && typeof value === "object") {
    return Object.keys(value)
      .map((item) => String(item).trim())
      .filter(Boolean);
  }
  return [];
};

const STATE_NAME_TO_ABBR = {
  ALABAMA: "AL",
  ALASKA: "AK",
  ARIZONA: "AZ",
  ARKANSAS: "AR",
  CALIFORNIA: "CA",
  COLORADO: "CO",
  CONNECTICUT: "CT",
  DELAWARE: "DE",
  "DISTRICT OF COLUMBIA": "DC",
  FLORIDA: "FL",
  GEORGIA: "GA",
  HAWAII: "HI",
  IDAHO: "ID",
  ILLINOIS: "IL",
  INDIANA: "IN",
  IOWA: "IA",
  KANSAS: "KS",
  KENTUCKY: "KY",
  LOUISIANA: "LA",
  MAINE: "ME",
  MARYLAND: "MD",
  MASSACHUSETTS: "MA",
  MICHIGAN: "MI",
  MINNESOTA: "MN",
  MISSISSIPPI: "MS",
  MISSOURI: "MO",
  MONTANA: "MT",
  NEBRASKA: "NE",
  NEVADA: "NV",
  "NEW HAMPSHIRE": "NH",
  "NEW JERSEY": "NJ",
  "NEW MEXICO": "NM",
  "NEW YORK": "NY",
  "NORTH CAROLINA": "NC",
  "NORTH DAKOTA": "ND",
  OHIO: "OH",
  OKLAHOMA: "OK",
  OREGON: "OR",
  PENNSYLVANIA: "PA",
  "RHODE ISLAND": "RI",
  "SOUTH CAROLINA": "SC",
  "SOUTH DAKOTA": "SD",
  TENNESSEE: "TN",
  TEXAS: "TX",
  UTAH: "UT",
  VERMONT: "VT",
  VIRGINIA: "VA",
  WASHINGTON: "WA",
  "WEST VIRGINIA": "WV",
  WISCONSIN: "WI",
  WYOMING: "WY",
};

export const normalizeState = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  return STATE_NAME_TO_ABBR[raw.replace(/\./g, "").trim().toUpperCase()] || "";
};

export const normalizeZip5 = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length >= 5) return digits.slice(0, 5);
  if (digits.length === 4) return `0${digits}`;
  return "";
};

export const normalizeCity = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\b\d{5}(-\d{4})?\b/g, " ")
    .replace(/[,./#!$%^&*;:{}=\-_`~()]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const getUserTerritoryIds = (user) =>
  uniqStrings([...(safeArray(user?.territoryIds)), ...(safeArray(user?.territories))]);

export const getTerritoryLabel = (territory) =>
  territory?.name ||
  territory?.center?.name ||
  territory?.label ||
  (territory?.id ? String(territory.id) : null);

const getTerritoryStates = (territory) =>
  uniqStrings([
    ...toStringArray(territory?.states),
    territory?.state,
    territory?.center?.state,
  ])
    .map(normalizeState)
    .filter(Boolean);

const getTerritoryCities = (territory) =>
  uniqStrings([
    ...toStringArray(territory?.cities),
    territory?.city,
    territory?.center?.city,
    ...toStringArray(territory?.coverageCities),
  ])
    .map(normalizeCity)
    .filter(Boolean);

const getTerritoryZipCodes = (territory) =>
  uniqStrings([
    ...toStringArray(territory?.zipCodes),
    ...toStringArray(territory?.zipcodes),
    ...toStringArray(territory?.zips),
  ])
    .map(normalizeZip5)
    .filter(Boolean);

const detectTerritoryMatch = (territories, address) => {
  const list = Array.isArray(territories) ? territories : [];
  const city = normalizeCity(address?.addressCity);
  const state = normalizeState(address?.addressState);
  const zip = normalizeZip5(address?.addressZip);

  let best = null;
  let bestScore = 0;
  let bestMethod = "none";

  list.forEach((territory) => {
    if (!territory?.id) return;

    const states = getTerritoryStates(territory);
    if (states.length > 0 && state && !states.includes(state)) return;

    const zips = getTerritoryZipCodes(territory);
    const cities = getTerritoryCities(territory);

    let score = 0;
    let method = "none";

    if (zip && zips.includes(zip)) {
      score = 100;
      method = "zip";
    } else if (city && cities.includes(city)) {
      score = 80;
      method = "city";
    } else if (state && states.length > 0 && states.includes(state)) {
      score = 25;
      method = "state";
    }

    if (score > bestScore) {
      best = territory;
      bestScore = score;
      bestMethod = method;
      return;
    }

    if (score === bestScore && score > 0 && best) {
      const currentLabel = getTerritoryLabel(territory) || "";
      const bestLabel = getTerritoryLabel(best) || "";
      if (currentLabel.localeCompare(bestLabel) < 0) {
        best = territory;
        bestMethod = method;
      }
    }
  });

  return { match: bestScore > 0 ? best : null, method: bestMethod };
};

export const resolveAccountTerritory = ({ territories, address, assignedUser }) => {
  const territoryList = Array.isArray(territories) ? territories : [];
  const assignedIds = new Set(getUserTerritoryIds(assignedUser));
  const assignedTerritories = territoryList.filter((territory) =>
    assignedIds.has(String(territory?.id || ""))
  );

  const preferred = detectTerritoryMatch(assignedTerritories, address);
  if (preferred.match) {
    return {
      id: String(preferred.match.id),
      name: getTerritoryLabel(preferred.match),
      matchedBy: `assigned_${preferred.method}`,
      scope: "assigned-user",
    };
  }

  const fallback = detectTerritoryMatch(territoryList, address);
  if (fallback.match) {
    return {
      id: String(fallback.match.id),
      name: getTerritoryLabel(fallback.match),
      matchedBy: fallback.method,
      scope: "company",
    };
  }

  if (assignedTerritories.length === 1) {
    return {
      id: String(assignedTerritories[0].id),
      name: getTerritoryLabel(assignedTerritories[0]),
      matchedBy: "assigned_default",
      scope: "assigned-user",
    };
  }

  return null;
};
